import React, { useState } from "react";
import axios from "axios";
import "../../Style/TableStyle.scss";
import "../../Style/__searchbar.scss";
import "../../Style/__button.scss";
import "./BanksPage.scss";
import {
  deleteAlert,
  errorAlert,
  handleSuccess,
} from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { BsBank } from "react-icons/bs";
import DataGridServerSide from "../../Components/DataGridServerSide";
import CrudPageHeader from "../../Components/CrudPageHeader";
import useAccountsGridData from "../../hooks/useAccountsGridData";
import BanksModal from "./BanksModal";
import BanksViewModal from "./BanksViewModal";

export default function BanksPage() {
  const headers = authHeader();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [viewBank, setViewBank] = useState(null);

  const getApiErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.errorMessage) {
      return `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`;
    }

    if (
      typeof error?.response?.data === "string" &&
      error.response.data.trim() !== ""
    ) {
      return error.response.data;
    }

    if (error?.message) {
      return `Gabim: ${error.message}`;
    }

    return fallbackMessage;
  };

  const {
    loading,
    data,
    rowCount,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    quickFilter,
    setQuickFilter,
    fetchData,
  } = useAccountsGridData({
    headers,
    ledgerID: 102,
    onError: (error) =>
      errorAlert(
        getApiErrorMessage(error, "Gabim i panjohur në marrjen e bankave."),
      ),
  });

  const handleEdit = async (item) => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + `Accounts/getAccountForEdit/${item.AccountID}`,
        { headers },
      );

      setSelectedBank(response.data);
      setShowEditModal(true);
    } catch (error) {
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim në marrjen e të dhënave të bankës për editim.",
        ),
      );
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedBank(null);
  };

  const handleView = async (item) => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + `Accounts/getAccountForEdit/${item.AccountID}`,
        { headers },
      );

      setViewBank(response.data);
      setShowViewModal(true);
    } catch (error) {
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim në marrjen e të dhënave të bankës për shikim.",
        ),
      );
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewBank(null);
  };

  const handleDelete = async (deleteItemId) => {
    try {
      const response = await axios.delete(
        ApiLink.API_URL + "Accounts/payment" + `/${deleteItemId}`,
        {
          headers,
        },
      );

      const result = response.data;
      if (result.success) {
        handleSuccess("Banka është fshirë me sukses");
        fetchData();
      } else {
        deleteAlert(
          "Banka nuk mund të fshihet sepse është përdorur në transaksione",
        );
      }
    } catch (error) {
      deleteAlert("Ka ndodhur një gabim gjatë fshirjes së bankës.");
    }
  };

  const banksGridHeader = [
    {
      field: "id",
      headerName: "#",
      width: 60,
      headerClassName: "super-app-theme--header",
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
    },
    {
      field: "AccountName",
      headerName: "Emri i Bankës",
      flex: 2.4,
      minWidth: 300,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IdentificationNumber",
      headerName: "Numri i Llogarisë",
      flex: 1.2,
      minWidth: 180,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "Adress",
      headerName: "Adresa",
      flex: 1.6,
      minWidth: 220,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IsActive",
      headerName: "Aktive",
      width: 90,
      headerClassName: "super-app-theme--header",
      renderCell: (params) =>
        params.value ? (
          <CheckCircleOutlineIcon style={{ color: "green" }} />
        ) : (
          ""
        ),
    },
  ];

  const rows = data.map((item, index) => ({
    id: paginationModel.page * paginationModel.pageSize + index + 1,
    AccountName: item.AccountName,
    IdentificationNumber: item.IdentificationNumber,
    Adress: item.Adress ?? "",
    IsActive: Boolean(item.IsActive),
    deleteItemId: item.AccountID,
    rowInfo: item,
  }));

  return (
    <div className="Crud__Container BRP__page">
      <BanksModal
        show={showEditModal}
        onHide={handleCloseEditModal}
        mode="edit"
        initialData={selectedBank}
        fetchData={fetchData}
      />

      <BanksViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        bankData={viewBank}
      />

      <CrudPageHeader
        icon={BsBank}
        title="Lista e Bankave"
        description="Forma e Bankave: Menaxhoni, shtoni, editoni dhe fshini Bankat."
        tutorialUrl="https://www.youtube.com/@Accountree"
        tutorialLabel="Shiko Tutorialin"
        action={<BanksModal fetchData={fetchData} />}
        className="BRP__header"
      />

      <DataGridServerSide
        columns={banksGridHeader}
        rows={rows}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleShowPrint={handleView}
        showPayment={false}
        height={500}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        showDetailsStatus={true}
      />
    </div>
  );
}
